import React, { useState } from "react";
import { DownloadSimple, DeviceMobile, Share, CheckCircle, X, Copy } from "@phosphor-icons/react";
import { toast } from "@/lib/toast";
import { usePwaInstall } from "@/context/PwaInstallContext";

/**
 * "Get the App" surface for the portal sidebar footer.
 * • Installed → tiny green pill.
 * • Chrome/Edge/Android → button fires the deferred install prompt.
 * • iOS Safari or desktop → button opens a modal with tailored instructions.
 */
export default function GetAppLink({ compact = false }) {
  const { isInstalled, canInstall, isIOS, promptInstall } = usePwaInstall();
  const [modalOpen, setModalOpen] = useState(false);

  const handleClick = async () => {
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") return;
      if (outcome === "dismissed") return;
    }
    setModalOpen(true);
  };

  const copyLink = () => {
    const url = window.location.origin;
    try {
      navigator.clipboard.writeText(url);
      toast.success("Link copied — text it to yourself to open on your phone.");
    } catch {
      toast.error("Copy failed — the link is " + url);
    }
  };

  if (isInstalled) {
    return (
      <div
        className="flex items-center gap-2 text-[11px] text-emerald-300 font-semibold"
        data-testid="get-app-installed-badge"
      >
        <CheckCircle size={14} weight="fill" /> App installed
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="get-app-link"
        className={`flex items-center gap-2 text-white/80 hover:text-white transition-colors ${
          compact ? "text-[11px]" : "text-xs"
        } font-semibold`}
      >
        <DownloadSimple size={compact ? 13 : 14} weight="bold" />
        <span>Download MFG App</span>
      </button>

      {modalOpen && (
        <GetAppModal
          isIOS={isIOS}
          canInstall={canInstall}
          onInstall={async () => {
            const outcome = await promptInstall();
            if (outcome === "accepted" || outcome === "dismissed") setModalOpen(false);
          }}
          onCopyLink={copyLink}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

function GetAppModal({ isIOS, canInstall, onInstall, onCopyLink, onClose }) {
  const isDesktop = window.matchMedia("(min-width: 1024px)").matches
    && !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="get-app-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="get-app-modal-close"
          className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"
        >
          <X size={16} weight="bold" />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0A84FF] to-[#0058B0] text-white grid place-items-center">
              <DeviceMobile size={22} weight="bold" />
            </div>
            <div>
              <h2 className="font-display font-black text-lg">Get the MFG App</h2>
              <p className="text-xs text-neutral-500">Free · works offline · push alerts</p>
            </div>
          </div>

          <ul className="space-y-2 text-sm text-neutral-700 mb-5">
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" weight="fill" />
              <span>One-tap access from your phone&apos;s home screen</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" weight="fill" />
              <span>Upload BOLs even without signal &mdash; auto-syncs when you&apos;re back online</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" weight="fill" />
              <span>Instant push notifications for offers, invoices, POD approvals</span>
            </li>
          </ul>

          {canInstall && !isDesktop && (
            <button
              onClick={onInstall}
              data-testid="get-app-modal-install"
              className="w-full bg-[#0A84FF] hover:bg-[#0058B0] text-white font-semibold py-3 rounded-lg inline-flex items-center justify-center gap-2 mb-2"
            >
              <DownloadSimple size={16} weight="bold" /> Install Now
            </button>
          )}

          {isIOS && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm">
              <div className="font-semibold text-neutral-800 mb-2 flex items-center gap-2">
                <Share size={16} weight="bold" /> Install on iPhone / iPad
              </div>
              <ol className="text-neutral-600 space-y-1 pl-5 list-decimal">
                <li>Tap the <span className="font-semibold">Share</span> icon in Safari&apos;s bottom bar</li>
                <li>Scroll and tap <span className="font-semibold">Add to Home Screen</span></li>
                <li>Tap <span className="font-semibold">Add</span> &mdash; the MFG app appears on your home screen</li>
              </ol>
            </div>
          )}

          {isDesktop && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
              <div className="font-semibold mb-1">Best on your phone</div>
              <p className="mb-3">The offline BOL upload and push alerts are designed for a phone home-screen app. Send yourself the link:</p>
              <button
                onClick={onCopyLink}
                data-testid="get-app-modal-copy"
                className="inline-flex items-center gap-2 bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 px-3 py-2 rounded-md text-xs font-semibold"
              >
                <Copy size={14} weight="bold" /> Copy portal link
              </button>
            </div>
          )}

          {!isIOS && !isDesktop && !canInstall && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-600">
              Look for the <span className="font-semibold">Install</span> icon in your browser&apos;s
              address bar or menu &mdash; usually a small &quot;+&quot; or download arrow. Tap it to add MFG to your
              home screen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
